import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../axiosConfig';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Snackbar,
  Tooltip,
  Divider,
  Chip,
  InputAdornment,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const TierManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isSolo = location.pathname.startsWith('/solo');
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newTierName, setNewTierName] = useState('');
  const [newTierRate, setNewTierRate] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [editingRate, setEditingRate] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [recalcDialogOpen, setRecalcDialogOpen] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  const fetchTiers = useCallback(async () => {
    try {
      const response = await api.get('/calculator/tiers');
      setTiers(response.data || []);
    } catch (error) {
      showSnackbar('Failed to load tiers', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTiers();
  }, [fetchTiers]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAdd = async () => {
    const name = newTierName.trim();
    if (!name) return;
    setSaving(true);
    try {
      const response = await api.post('/calculator/tiers', {
        name,
        hourly_rate: parseFloat(newTierRate) || 0
      });
      setTiers(prev => [...prev, response.data]);
      setNewTierName('');
      setNewTierRate('');
      showSnackbar(`Tier "${name}" added`);
    } catch (error) {
      showSnackbar(error.response?.data?.error || 'Failed to add tier', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEditStart = (tier) => {
    setEditingId(tier.id);
    setEditingName(tier.name);
    setEditingRate(tier.hourly_rate ?? '');
  };

  const handleEditSave = async (tier) => {
    const name = editingName.trim();
    if (!name) return;
    setSaving(true);
    try {
      const response = await api.put(`/calculator/tiers/${tier.id}`, {
        name,
        hourly_rate: parseFloat(editingRate) || 0
      });
      setTiers(prev => prev.map(t => t.id === tier.id ? response.data : t));
      setEditingId(null);
      showSnackbar('Tier updated');
    } catch (error) {
      showSnackbar(error.response?.data?.error || 'Failed to update tier', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditingName('');
    setEditingRate('');
  };

  const handleDelete = async (tier) => {
    setSaving(true);
    try {
      await api.delete(`/calculator/tiers/${tier.id}`);
      setTiers(prev => prev.filter(t => t.id !== tier.id));
      showSnackbar(`Tier "${tier.name}" deleted`);
    } catch (error) {
      showSnackbar(error.response?.data?.error || 'Failed to delete tier', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleMove = async (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= tiers.length) return;

    const reordered = [...tiers];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(newIndex, 0, moved);

    const updated = reordered.map((t, i) => ({ ...t, sort_order: i }));
    setTiers(updated);

    setSaving(true);
    try {
      await Promise.all([
        api.put(`/calculator/tiers/${updated[newIndex].id}`, { sort_order: newIndex }),
        api.put(`/calculator/tiers/${updated[index].id}`, { sort_order: index }),
      ]);
    } catch (error) {
      showSnackbar('Failed to save order', 'error');
      fetchTiers();
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e, tier) => {
    if (e.key === 'Enter') handleEditSave(tier);
    if (e.key === 'Escape') handleEditCancel();
  };

  const handleAddKeyDown = (e) => {
    if (e.key === 'Enter') handleAdd();
  };

  const handleRecalculateAll = async () => {
    setRecalculating(true);
    try {
      const response = await api.post('/calculator/services/calculate-all', { profit_level: 1 });
      const { total_services, successful, failed } = response.data || {};
      if (failed > 0) {
        showSnackbar(
          `Recalculated ${successful} of ${total_services} services — ${failed} failed. Check those services individually.`,
          'warning'
        );
      } else {
        showSnackbar(`Recalculated all ${successful} services successfully.`);
      }
    } catch (error) {
      showSnackbar(error.response?.data?.error || 'Failed to recalculate services', 'error');
    } finally {
      setRecalculating(false);
      setRecalcDialogOpen(false);
    }
  };

  const backPath = location.pathname.startsWith('/solo') ? '/solo/calculator' : '/calculator';

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: 2,
        p: 3,
        mb: 3,
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <IconButton onClick={() => navigate(backPath)} sx={{ color: 'white' }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h5" fontWeight={600}>Pricing Tiers</Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            {isSolo
              ? "Set your hourly rate so the calculator can work out accurate prices."
              : "Define the tier levels and hourly rates used to price services across your team"}
          </Typography>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        {isSolo
          ? "Your hourly rate is used by the calculator to work out the labour cost for each service."
          : "Each tier has a name and an hourly rate. The hourly rate is used by the calculator to work out the labour cost per service. Employees are assigned a tier on their Employee Card."}
      </Alert>

      {/* Hourly rate guidance */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Enter {isSolo ? 'your own' : "each employee's"} <strong>gross hourly wage</strong> —
          i.e. what {isSolo ? 'you earn' : 'the employee earns'} before tax. The calculator
          automatically adds employer National Insurance, pension contributions, and holiday
          pay accrual on top when working out true labour cost — you don't need to adjust the
          rate yourself.
        </Typography>
      </Alert>

      {/* Recalculate all prices */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => setRecalcDialogOpen(true)}
          disabled={loading || tiers.length === 0}
        >
          Recalculate All Prices
        </Button>
      </Box>

      <Dialog open={recalcDialogOpen} onClose={() => !recalculating && setRecalcDialogOpen(false)}>
        <DialogTitle>Recalculate all service prices?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This re-runs the cost calculation for every active service using your current tier
            rates, overheads, and profit target — including employer National Insurance, pension,
            and holiday pay on top of each tier's hourly rate. Recommended prices will update.
            Prices you actually charge (Your Price) are not changed automatically.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecalcDialogOpen(false)} disabled={recalculating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRecalculateAll}
            disabled={recalculating}
            startIcon={recalculating ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {recalculating ? 'Recalculating…' : 'Recalculate All'}
          </Button>
        </DialogActions>
      </Dialog>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>

          {/* Column headers */}
          {tiers.length > 0 && (
            <>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1,
                backgroundColor: '#f5f5f5',
                gap: 1
              }}>
                <Box sx={{ width: 32 }} />
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1, fontWeight: 600 }}>
                  TIER NAME
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ width: 130, fontWeight: 600 }}>
                  HOURLY RATE
                </Typography>
                <Box sx={{ width: 160 }} />
              </Box>
              <Divider />
            </>
          )}

          {tiers.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary" gutterBottom>
                No tiers set up yet.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isSolo
                  ? "Add your hourly rate below to get started."
                  : "Add your first tier below — start with your lowest level (e.g. Stylist)."}
              </Typography>
            </Box>
          )}

          {tiers.map((tier, index) => (
            <React.Fragment key={tier.id}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1.5,
                gap: 1,
                '&:hover': { backgroundColor: '#f9f9f9' }
              }}>
                <Chip
                  label={index + 1}
                  size="small"
                  sx={{
                    minWidth: 32,
                    backgroundColor: '#667eea',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }}
                />

                {editingId === tier.id ? (
                  <>
                    <TextField
                      value={editingName}
                      onChange={e => setEditingName(e.target.value)}
                      onKeyDown={e => handleKeyDown(e, tier)}
                      size="small"
                      sx={{ flex: 1 }}
                      inputProps={{ maxLength: 100 }}
                      autoFocus
                      placeholder="Tier name"
                    />
                    <TextField
                      value={editingRate}
                      onChange={e => setEditingRate(e.target.value)}
                      onKeyDown={e => handleKeyDown(e, tier)}
                      size="small"
                      type="number"
                      sx={{ width: 130 }}
                      inputProps={{ min: 0, step: 0.01 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">£</InputAdornment>
                      }}
                      placeholder="0.00"
                    />
                  </>
                ) : (
                  <>
                    <Typography sx={{ flex: 1, fontWeight: 500 }}>{tier.name}</Typography>
                    <Box sx={{ width: 130 }}>
                      {tier.hourly_rate > 0 ? (
                        <Typography variant="body2">
                          £{parseFloat(tier.hourly_rate).toFixed(2)}/hr
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled">Not set</Typography>
                      )}
                    </Box>
                  </>
                )}

                {editingId === tier.id ? (
                  <>
                    <Tooltip title="Save">
                      <span>
                        <IconButton size="small" color="success"
                          onClick={() => handleEditSave(tier)} disabled={saving}>
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton size="small" onClick={handleEditCancel}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  <>
                    <Tooltip title="Move up">
                      <span>
                        <IconButton size="small"
                          onClick={() => handleMove(index, -1)}
                          disabled={index === 0 || saving}>
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move down">
                      <span>
                        <IconButton size="small"
                          onClick={() => handleMove(index, 1)}
                          disabled={index === tiers.length - 1 || saving}>
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEditStart(tier)} disabled={saving}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <span>
                        <IconButton size="small" color="error"
                          onClick={() => handleDelete(tier)} disabled={saving}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                )}
              </Box>
              {index < tiers.length - 1 && <Divider />}
            </React.Fragment>
          ))}

          {/* Add new tier */}
          <Divider />
          <Box sx={{ p: 2 }}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs>
                <TextField
                  fullWidth
                  value={newTierName}
                  onChange={e => setNewTierName(e.target.value)}
                  onKeyDown={handleAddKeyDown}
                  placeholder="Tier name — e.g. Senior Stylist"
                  size="small"
                  inputProps={{ maxLength: 100 }}
                  disabled={saving}
                />
              </Grid>
              <Grid item sx={{ width: 140 }}>
                <TextField
                  fullWidth
                  value={newTierRate}
                  onChange={e => setNewTierRate(e.target.value)}
                  onKeyDown={handleAddKeyDown}
                  placeholder="0.00"
                  size="small"
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">£/hr</InputAdornment>
                  }}
                  disabled={saving}
                />
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                  onClick={handleAdd}
                  disabled={!newTierName.trim() || saving}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    whiteSpace: 'nowrap',
                    height: 40
                  }}
                >
                  Add Tier
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TierManagement;